import { useCallback } from 'react';
import { useToast } from '../../components/ui/Toast';

/**
 * Sales Tools — Phase 3.3.
 * Thin wrapper around /api/merchant/orders/:orderId/payment-link and
 * /api/merchant/payment-links/*. Owns toast feedback so callers don't
 * have to repeat it. Returns the shaped link on success, null on
 * failure.
 */
export default function usePaymentLinkActions() {
  const toast = useToast();

  const createLink = useCallback(
    async (orderId, { expiresInHours } = {}) => {
      try {
        const resp = await fetch(
          `/api/merchant/orders/${encodeURIComponent(orderId)}/payment-link`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(expiresInHours ? { expiresInHours } : {}),
          }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not create payment link');
          return null;
        }
        return data.link;
      } catch {
        toast.error('Network error');
        return null;
      }
    },
    [toast]
  );

  const listLinksForOrder = useCallback(async (orderId) => {
    try {
      const resp = await fetch(
        `/api/merchant/payment-links?orderId=${encodeURIComponent(orderId)}`,
        { credentials: 'include' }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return [];
      return Array.isArray(data.links) ? data.links : [];
    } catch {
      return [];
    }
  }, []);

  const emailLink = useCallback(
    async (linkId, to) => {
      try {
        const resp = await fetch(
          `/api/merchant/payment-links/${encodeURIComponent(linkId)}/email`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(to ? { to } : {}),
          }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not send email');
          return null;
        }
        toast.success('Payment link emailed');
        return data.link;
      } catch {
        toast.error('Network error');
        return null;
      }
    },
    [toast]
  );

  const cancelLink = useCallback(
    async (linkId) => {
      try {
        const resp = await fetch(
          `/api/merchant/payment-links/${encodeURIComponent(linkId)}/cancel`,
          { method: 'POST', credentials: 'include' }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not cancel link');
          return false;
        }
        toast.success('Payment link cancelled');
        return true;
      } catch {
        toast.error('Network error');
        return false;
      }
    },
    [toast]
  );

  return { createLink, listLinksForOrder, emailLink, cancelLink };
}
